import type { ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Briefcase, User } from "lucide-react";
import { useCommission } from "@/hooks/use-api";
import { DetailPage } from "@/components/v2/detail-page";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { KPIRow } from "@/components/v2/kpi-row";
import { StatusBadge } from "@/components/status-badge";
import { formatCurrency, formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

// LinkedChip renders a clickable record (button → navigate) when `onClick` is
// provided, otherwise a static, display-only chip — used for the recipient team
// member, which has no detail route yet.
function LinkedChip({
  icon,
  label,
  sublabel,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  sublabel: string;
  onClick?: () => void;
}) {
  const className =
    "flex items-center gap-2 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2 text-left w-full sm:w-1/2";
  const body = (
    <>
      <span className="shrink-0 text-[color:var(--color-text-md)]">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block truncate typo-body font-medium text-[color:var(--color-text-hi)]">
          {label}
        </span>
        <span className="block typo-label text-[color:var(--color-text-md)]">
          {sublabel}
        </span>
      </span>
    </>
  );

  if (!onClick) {
    return <div className={className}>{body}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${className} hover:bg-[color:var(--color-elevated)]`}
    >
      {body}
    </button>
  );
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// commission_rate is stored as a decimal (0.1 = 10%). Display as a percent —
// pure display formatting, permitted by the database-first-calculations rule.
function formatRate(rate: number | null): string {
  if (rate == null) return "—";
  return `${+(rate * 100).toFixed(2)}%`;
}

// finance.commissions has no human-readable number column, so derive a stable
// display id from the UUID tail (mirrors the warranty WTY- pattern).
function displayId(id: string): string {
  return `CMN-${id.slice(-6).toUpperCase()}`;
}

export default function V2CommissionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useCommission(id ?? "");
  const commission = (data as { commission?: Row } | undefined)?.commission;

  const status = str(commission?.status);
  const opportunityId = str(commission?.opportunity_id);
  const opportunityName = str(commission?.opportunity_name);
  const teamMemberName = str(commission?.team_member_name);
  const baseAmount = num(commission?.base_amount);
  const commissionAmount = num(commission?.commission_amount);
  const bonusAmount = num(commission?.bonus_amount);
  const totalAmount = num(commission?.total_amount);
  const rate = num(commission?.commission_rate);
  const payPeriodStart = str(commission?.pay_period_start);
  const payPeriodEnd = str(commission?.pay_period_end);
  const paidAt = str(commission?.paid_at);

  const title = commission?.id
    ? `Commission ${displayId(commission.id as string)}`
    : "Commission";

  const payPeriod =
    payPeriodStart || payPeriodEnd
      ? `${payPeriodStart ? formatDate(payPeriodStart) : "—"} – ${
          payPeriodEnd ? formatDate(payPeriodEnd) : "—"
        }`
      : "—";

  return (
    <DetailPage
      isLoading={isLoading}
      isError={isError}
      isNotFound={!isLoading && !isError && !commission}
      title={title}
      subtitle={teamMemberName ?? undefined}
      breadcrumbs={[
        { label: "Commissions", href: "/commissions" },
        { label: title },
      ]}
      notFoundMessage={{
        title: "Commission not found",
        description: "This commission may have been deleted.",
      }}
      actions={<DetailPageActions entityLabel="Commission" />}
    >
      <div className="flex gap-6">
        <div className="flex-1 min-w-0 space-y-5">
          {/* Inline properties strip */}
          <div className="flex items-center gap-1.5 flex-wrap typo-body text-[color:var(--color-text-md)]">
            {status && <StatusBadge status={status} />}
            {opportunityName && (
              <>
                <span className="text-[color:var(--color-text-lo)]">·</span>
                <span className="truncate">{opportunityName}</span>
              </>
            )}
            {commission?.created_at != null && (
              <>
                <span className="text-[color:var(--color-text-lo)]">·</span>
                <span>
                  Created {formatDate(commission.created_at as string)}
                </span>
              </>
            )}
          </div>

          {/* KPI row */}
          <KPIRow
            cards={[
              {
                label: "Total",
                value: totalAmount != null ? formatCurrency(totalAmount) : "—",
              },
              {
                label: "Status",
                value: status ? titleCase(status) : "—",
              },
              {
                label: "Rate",
                value: formatRate(rate),
              },
              {
                label: "Base Amount",
                value: baseAmount != null ? formatCurrency(baseAmount) : "—",
              },
            ]}
          />

          {/* Amounts */}
          <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
            <div className="px-4 pt-3">
              <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                Amounts
              </span>
            </div>
            <div className="px-4 py-2">
              <InfoRow
                label="Base Amount"
                value={baseAmount != null ? formatCurrency(baseAmount) : null}
              />
              <InfoRow
                label="Rate"
                value={rate != null ? formatRate(rate) : null}
              />
              <InfoRow
                label="Commission"
                value={
                  commissionAmount != null
                    ? formatCurrency(commissionAmount)
                    : null
                }
              />
              <InfoRow
                label="Bonus"
                value={bonusAmount != null ? formatCurrency(bonusAmount) : null}
              />
              <InfoRow
                label="Total"
                value={totalAmount != null ? formatCurrency(totalAmount) : null}
              />
            </div>
          </div>

          {/* Status & pay period — read-only status history surrogate. There is
              no separate commission status-history table; the lifecycle
              (pending → payable → paid / cancelled) is expressed via the current
              status and the paid_at timestamp. */}
          <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
            <div className="px-4 pt-3">
              <span className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide">
                Status & Pay Period
              </span>
            </div>
            <div className="px-4 py-2">
              <InfoRow
                label="Status"
                value={status ? titleCase(status) : null}
              />
              <InfoRow
                label="Pay Period"
                value={payPeriod === "—" ? null : payPeriod}
              />
              <InfoRow
                label="Paid At"
                value={paidAt ? formatDate(paidAt) : "Not yet paid"}
              />
            </div>
          </div>

          {/* Linked records */}
          {(opportunityId || teamMemberName) && (
            <div className="flex flex-wrap gap-2">
              {opportunityId && (
                <LinkedChip
                  icon={<Briefcase className="h-4 w-4" />}
                  label={opportunityName ?? "View opportunity"}
                  sublabel="Opportunity"
                  onClick={() => navigate(`/opportunities/${opportunityId}`)}
                />
              )}
              {teamMemberName && (
                // Recipient team member — display-only: no team-member detail
                // route exists yet (only a list page).
                <LinkedChip
                  icon={<User className="h-4 w-4" />}
                  label={teamMemberName}
                  sublabel="Recipient"
                />
              )}
            </div>
          )}

          {/* No activity feed — `commission` is not in the
              core.communications.entity_type enum, so EntityActivityFeed cannot
              mount (mirrors warranties/detail.tsx, ADV-1144). */}
        </div>

        {/* Right rail */}
        <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
          <RailSection title="Properties">
            <InfoRow label="Status" value={status ? titleCase(status) : null} />
            <InfoRow label="Opportunity" value={opportunityName} />
            <InfoRow label="Recipient" value={teamMemberName} />
            <InfoRow
              label="Rate"
              value={rate != null ? formatRate(rate) : null}
            />
            <InfoRow
              label="Total"
              value={totalAmount != null ? formatCurrency(totalAmount) : null}
            />
            <InfoRow
              label="Pay Period"
              value={payPeriod === "—" ? null : payPeriod}
            />
            <InfoRow
              label="Paid At"
              value={paidAt ? formatDate(paidAt) : null}
            />
            <InfoRow
              label="Created"
              value={
                commission?.created_at
                  ? formatDate(commission.created_at as string)
                  : null
              }
            />
          </RailSection>
        </div>
      </div>
    </DetailPage>
  );
}
